import { useMemo, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { fireInitiateCheckout, firePurchase, fireLeadEvent } from "./components";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PRESETS = [15, 30, 45, 60];
const MIN_MINUTES = 5;
const MAX_MINUTES = 120;
const RATE = 1; // $1/min

export default function ClaritySessionBooking() {
  const [minutes, setMinutes] = useState(30);
  const [form, setForm] = useState({ email: "" });
  const [step, setStep] = useState("choose"); // choose | pay | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const price = useMemo(() => Math.round(Math.max(MIN_MINUTES, Math.min(MAX_MINUTES, Number(minutes) || MIN_MINUTES))) * RATE, [minutes]);
  const canPay = /\S+@\S+\.\S+/.test(form.email) && minutes >= MIN_MINUTES;

  // Virtual "product" used by Meta/GA4 helpers so the Clarity Session fires the same events as other purchases
  const trackingProduct = {
    id: "clarity-session",
    name: `Clarity Session · ${minutes} min`,
    price,
    category: "Clarity Session",
  };

  const handleContinue = () => {
    fireLeadEvent(trackingProduct);
    fireInitiateCheckout(trackingProduct);
    setStep("pay");
  };

  const minutesClamped = Math.round(Math.max(MIN_MINUTES, Math.min(MAX_MINUTES, Number(minutes) || MIN_MINUTES)));

  return (
    <div className="sf-card jewel-card rounded-sm p-8 sticky top-28" style={{ ["--jewel"]: "#F5D48A" }} data-testid="clarity-session-booking">
      {step !== "success" && (
        <>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8] mb-2">Reserve your minutes</div>
          <div className="flex items-baseline gap-1 mb-6">
            <span className="serif text-5xl gold-shimmer" data-testid="clarity-live-total">${price}</span>
            <span className="text-[#A89FA8] text-sm ml-2">· {minutesClamped} min · $1 / min</span>
          </div>
        </>
      )}

      {step === "choose" && (
        <>
          <label className="form-label">How many minutes?</label>
          <div className="grid grid-cols-4 gap-2 mb-3" data-testid="clarity-presets">
            {PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMinutes(m)}
                data-testid={`clarity-preset-${m}`}
                className={`py-3 text-sm rounded-sm border transition-all ${
                  Number(minutes) === m
                    ? "bg-[#B58B2E]/20 border-[#F5D48A] text-[#F5D48A]"
                    : "border-[#B58B2E]/30 text-[#F2EFE8]/70 hover:border-[#F5D48A]/60"
                }`}
              >
                {m} min
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs text-[#A89FA8] uppercase tracking-widest">Or</span>
            <input
              type="number"
              min={MIN_MINUTES}
              max={MAX_MINUTES}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              className="form-input flex-1"
              placeholder={`${MIN_MINUTES}-${MAX_MINUTES} minutes`}
              data-testid="clarity-minutes-input"
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label flex items-center gap-2"><Mail size={13} /> Email</label>
              <input
                required
                type="email"
                data-testid="clarity-email"
                className="form-input"
                placeholder="you@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <button
              disabled={!canPay}
              onClick={handleContinue}
              className="btn-gold w-full mt-2 disabled:opacity-40"
              data-testid="clarity-continue"
            >
              Continue to PayPal <ArrowRight size={14} />
            </button>
            <p className="text-[10px] text-center text-[#A89FA8] tracking-widest uppercase pt-2">
              Secured by PayPal · SSL Encrypted
            </p>
            <p className="text-[11px] text-center text-[#F2EFE8]/60 pt-1 leading-relaxed">
              Not satisfied? Full refund.
            </p>
          </div>
        </>
      )}

      {step === "pay" && PAYPAL_CLIENT_ID && (
        <>
          <div className="mb-4 pb-4 border-b border-[#B58B2E]/25">
            <p className="text-xs text-[#A89FA8] uppercase tracking-widest mb-1">Reserving</p>
            <p className="text-[#F2EFE8]">{minutesClamped} minutes with Dawn</p>
            <p className="text-[#F2EFE8]/70 text-sm">{form.email}</p>
          </div>
          <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
              createOrder={async () => {
                const r = await axios.post(`${API}/paypal/create-order`, {
                  product_id: "clarity-session",
                  product_name: `Clarity Session · ${minutesClamped} min with Dawn Delisle`,
                  price,
                  minutes: minutesClamped,
                  customer_name: "",
                  customer_email: form.email.trim(),
                });
                return r.data.id;
              }}
              onApprove={async (data) => {
                try {
                  const r = await axios.post(`${API}/clarity-sessions/capture`, {
                    order_id: data.orderID,
                    minutes: minutesClamped,
                    price,
                    customer_email: form.email.trim(),
                  });
                  setConfirmation(r.data);
                  firePurchase(trackingProduct, data.orderID);
                  setStep("success");
                } catch (e) {
                  setErrorMsg("Your PayPal payment went through, but we hit a snag saving your request. Please email Dawn directly and she'll make it right.");
                  setStep("error");
                }
              }}
              onError={() => {
                setErrorMsg("PayPal encountered an error. Please try again or use a different payment method.");
                setStep("error");
              }}
              onCancel={() => setStep("choose")}
            />
          </PayPalScriptProvider>
          <button
            onClick={() => setStep("choose")}
            className="text-xs tracking-widest uppercase text-[#A89FA8] hover:text-[#F5D48A] mt-4 w-full text-center"
            data-testid="clarity-back"
          >
            ← Back to details
          </button>
        </>
      )}

      {step === "success" && confirmation && (
        <div className="text-center py-4" data-testid="clarity-success">
          <CheckCircle2 size={48} className="text-[#F5D48A] mx-auto mb-4" />
          <h3 className="serif text-3xl text-[#F2EFE8] mb-2">Reservation Received 🌸</h3>
          <p className="text-[#F2EFE8]/75 leading-relaxed mb-6">
            Thank you{confirmation.customer_name ? `, ${confirmation.customer_name.split(" ")[0]}` : ""}. Dawn personally reads every single request and will email you to arrange your {minutesClamped}-minute conversation.
          </p>
          <div className="bg-[#B58B2E]/10 border border-[#B58B2E]/25 rounded-sm p-4 text-left space-y-2 mb-4">
            <div className="flex justify-between text-sm"><span className="text-[#A89FA8]">Minutes reserved</span><span className="text-[#F2EFE8]">{minutesClamped}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#A89FA8]">Total paid</span><span className="text-[#F5D48A]">${price}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#A89FA8]">Confirmation</span><span className="text-[#F2EFE8] font-mono text-xs">{confirmation.confirmation_code}</span></div>
          </div>
          <p className="text-[10px] text-[#A89FA8] tracking-widest uppercase">
            A copy was sent to your PayPal receipt email
          </p>
        </div>
      )}

      {step === "error" && (
        <div className="text-center py-4" data-testid="clarity-error">
          <h3 className="serif text-2xl text-[#F5D48A] mb-3">Something went sideways</h3>
          <p className="text-[#F2EFE8]/75 text-sm leading-relaxed mb-4">{errorMsg}</p>
          <button onClick={() => setStep("choose")} className="btn-outline-gold">Try Again</button>
        </div>
      )}
    </div>
  );
}
